<script setup lang="ts">
/**
 * 〈市集 › 擴充詳情〉（`/plugins/:id`；批153 批A）——admin 以上。
 *
 * - **資訊卡**：key、產品、發行者、呈現欄位（〈編輯〉modal）；停權／恢復要營運主管。
 * - **發布主機（dl）同步**：dl 上 channel 的存取與期望值、最近錯誤、〈重試同步〉（＝立即跑一輪對帳）。
 * - **上架**：只有營運主管以上看得到按鈕；公開前要逐字輸入 plugin key（公開＝對全世界發布）。
 * - **版本**：拖放上傳 `.ipoplug`（`lib/fileDrop.ts`，拖不到檔案時走 `onNoFile` 提示）、版本表、
 *   改說明、撤回（`dialogs.prompt` 收理由；撤回的版號不可再用）。
 * - **權利**：這支 plugin 指派給哪些客戶（指派 modal、撤銷）。
 *
 * 批B：
 * - **上架**：第三方 plugin 要等發行者在 portal 申請（`listing_status='pending'`），這時〈核准上架〉
 *   就是 #14 publish；也可以〈婉拒〉（理由必填）。第一方照舊直接上架。
 * - **版本**：第三方公開 plugin 的新版先進審核（`review_status='pending'`）——狀態欄印「待審核」，
 *   營運主管可在這裡或〈市集 › 審核〉核准／婉拒；每一版都能〈下載〉（token 不進 URL）。
 * - **指派碼**：發碼（明碼只顯示一次）、清單、作廢。指派碼只用在私人 plugin。
 * - **申請上架期間凍結上傳**（補充契約 C3）：第三方 plugin 申請中時上傳區停用，說明固定在拖放區裡
 *   （兩層文字疊在同一格、只切換顯示，拖放區的大小不變、下方的版本表不跳動）。
 *
 * 真正的閘全在後端；這裡只決定按鈕給不給看。規格：`license-server/docs/PLUGIN_MARKET.md`。
 */
import { computed, onMounted, ref, watch } from 'vue'
import { onBeforeRouteLeave, onBeforeRouteUpdate, useRoute, useRouter } from 'vue-router'

import UiButton from '@/components/UiButton.vue'
import UiModal from '@/components/UiModal.vue'
import * as pm from '@/api/pluginMarket'
import type { GrantCode, IssuedGrantCode, MarketVersion, PluginDetail, PluginGrant } from '@/api/pluginMarket'
import { customerOptionLabel, isMarketCandidate } from '@/lib/customerLabel'
import { copyText, saveBlobFile } from '@/lib/download'
import { vEnter } from '@/lib/enterKey'
import { useFileDrop } from '@/lib/fileDrop'
import {
  apiErrorText,
  APPROVAL_PENDING_TEXT,
  canApproveListing,
  canManageMarket,
  canPublish,
  canVoidGrantCode,
  canWithdraw,
  dateToExpiry,
  DL_SYNC_LABELS,
  dlSyncState,
  emptyGrantCodeForm,
  formatBytes,
  formatFingerprint,
  GRANT_CODE_ERROR_TEXT,
  GRANT_CODE_MAX_COUNT,
  GRANT_CODE_NOTE_MAX,
  GRANT_SOURCE_LABELS,
  GRANT_STATE_LABELS,
  GRANT_STATE_PILL,
  grantCodeBody,
  grantCodeDateBounds,
  grantCodeDisplay,
  grantCodeFormError,
  grantState,
  grantTermLabel,
  hasReadyVersion,
  isApproving,
  isPendingReview,
  isThirdParty,
  issuedCodesText,
  isUploadFrozen,
  labelOf,
  LISTING_REVIEW_FREEZE_TEXT,
  LISTING_STATUS_LABELS,
  LISTING_STATUS_PILL,
  maskedGrantCode,
  packageFilename,
  PLUGIN_STATUS_LABELS,
  publishConfirmOk,
  REVIEW_ERROR_TEXT,
  REVIEW_STATUS_LABELS,
  saveNotesWithRetry,
  sortOrderError,
  UPLOAD_ERROR_TEXT,
  versionDisplay,
  VISIBILITY_LABELS,
  WITHDRAW_BLOCKED_APPROVING,
} from '@/lib/pluginMarket'
import { setPendingReviews } from '@/lib/reviewBadge'
import { safeHttpUrl } from '@/lib/safeUrl'
import { fmtSeen } from '@/lib/staleness'
import { useAuthStore } from '@/stores/auth'
import { useDataStore } from '@/stores/data'
import { useDialogsStore } from '@/stores/dialogs'
import { vCardTable } from '@/lib/cardTable'

const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const store = useDataStore()
const dialogs = useDialogsStore()

const id = computed(() => String(route.params.id ?? ''))
const plugin = ref<PluginDetail | null>(null)
const loading = ref(false)
const loadError = ref('')

const isManager = computed(() => canManageMarket(auth.role))
const showPublish = computed(() => canPublish(auth.role))

/** 這一頁會碰到的批B 錯誤碼（PLUGIN_MARKET.md §15.5）；其餘一律顯示後端的 `message`。 */
const KNOWN_ERRORS = { ...REVIEW_ERROR_TEXT, ...GRANT_CODE_ERROR_TEXT, ...UPLOAD_ERROR_TEXT }
function errText(e: unknown, fallback: string): string {
  return apiErrorText(e, KNOWN_ERRORS, fallback)
}
async function fail(e: unknown, fallback: string) {
  await dialogs.notify({ title: '操作失敗', message: errText(e, fallback) })
}

/**
 * 請求序號（R3-4）：換了 plugin（同一個元件被路由重用）之後，前一支 plugin 的慢回應一律丟掉，
 * 不然會把 A 的版本、權利、指派碼畫在 B 的頁面上。
 */
let loadSeq = 0
async function load() {
  if (!id.value) return
  const my = ++loadSeq
  const pid = id.value
  loading.value = true
  try {
    const p = await pm.getPlugin(pid)
    if (my !== loadSeq || pid !== id.value) return
    plugin.value = p
    loadError.value = ''
  } catch (e) {
    if (my !== loadSeq || pid !== id.value) return
    loadError.value = errText(e, '讀取擴充失敗')
  } finally {
    if (my === loadSeq) loading.value = false
  }
}
watch(id, () => {
  // 換 plugin：先清掉上一支的資料與游標，再重讀。
  plugin.value = null
  loadError.value = ''
  codes.value = []
  codeCursor.value = null
  codesError.value = ''
  grants.value = []
  grantCursor.value = null
  grantsError.value = ''
  void load()
  void loadGrants(true)
  void loadCodes(true)
})
onMounted(async () => {
  void store.ensureProducts().catch(() => {})
  void store.ensureCustomers().catch(() => {})
  await Promise.all([load(), loadGrants(true), loadCodes(true)])
})

/** 第三方 plugin（批B：上架要先申請、公開後每一版要審）。 */
const thirdParty = computed(() => (plugin.value ? isThirdParty(plugin.value) : false))
/** 公開中的第三方：之後上傳的版本先進審核。 */
const reviewedUploads = computed(() => thirdParty.value && plugin.value?.visibility === 'public')

/** 審核頁的徽章跟著更新（核准／婉拒後側欄數字當場變）。 */
async function refreshReviewBadge() {
  if (!isManager.value) return
  try {
    const q = await pm.listReviews()
    setPendingReviews(q.listing_pending.length + q.version_pending.length)
  } catch {
    /* 下一輪側欄輪詢會補上 */
  }
}

const sync = computed(() => (plugin.value ? dlSyncState(plugin.value) : 'none'))

// ---------------------------------------------------------------------------
// 資訊卡：編輯呈現欄位、停權／恢復
// ---------------------------------------------------------------------------
const editOpen = ref(false)
const editBusy = ref(false)
const edit = ref({ name: '', summary: '', description: '', homepage: '', sortOrder: '100', icon: '' as string | null })
function openEdit() {
  const p = plugin.value
  if (!p) return
  edit.value = {
    name: p.name,
    summary: p.summary,
    description: p.description_md,
    homepage: p.homepage_url ?? '',
    sortOrder: String(p.sort_order),
    icon: p.icon_data_uri,
  }
  editOpen.value = true
}
const editInvalid = computed(() => {
  const f = edit.value
  if (!f.name.trim()) return '名稱不可空白'
  if (f.homepage.trim() && !/^https:\/\//.test(f.homepage.trim())) return '首頁網址只收 https://'
  // R3-18：空字串不可以當成 0（`Number('')` 是 0，會排到最前面）。
  return sortOrderError(f.sortOrder)
})
const ICON_TYPES = ['image/png', 'image/webp', 'image/jpeg']
async function pickIcon(ev: Event) {
  const f = (ev.target as HTMLInputElement).files?.[0]
  ;(ev.target as HTMLInputElement).value = ''
  if (!f) return
  if (!ICON_TYPES.includes(f.type) || f.size > 16 * 1024) {
    await dialogs.notify({ title: '圖示不合規格', message: '圖示只收 png／webp／jpeg、16 KB 以內。' })
    return
  }
  edit.value.icon = await new Promise<string>((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => resolve(String(r.result))
    r.onerror = () => reject(r.error)
    r.readAsDataURL(f)
  })
}
async function saveEdit() {
  const p = plugin.value
  if (!p || editInvalid.value) return
  editBusy.value = true
  try {
    plugin.value = await pm.updatePlugin(p.id, {
      name: edit.value.name.trim(),
      summary: edit.value.summary.trim(),
      description_md: edit.value.description,
      homepage_url: edit.value.homepage.trim() || null,
      sort_order: Number(edit.value.sortOrder.trim()),
      icon_data_uri: edit.value.icon || null,
    })
    editOpen.value = false
  } catch (e) {
    await fail(e, '儲存失敗')
  } finally {
    editBusy.value = false
  }
}

const statusBusy = ref(false)
async function toggleStatus() {
  const p = plugin.value
  if (!p) return
  const suspend = p.status !== 'suspended'
  const ok = await dialogs.confirm({
    title: suspend ? `停權「${p.name}」` : `恢復「${p.name}」`,
    message: suspend
      ? '停權後所有有權的客戶立刻看不到、下載不到這個擴充（公開的也收回匿名下載）；已安裝的站台照常運作。確定要停權嗎？'
      : '恢復後有權的客戶又能列出與下載；公開的會重新出現在市集。確定要恢復嗎？',
    danger: suspend,
    confirmText: suspend ? '停權' : '恢復',
  })
  if (!ok) return
  statusBusy.value = true
  try {
    plugin.value = await pm.updatePlugin(p.id, { status: suspend ? 'suspended' : 'active' })
  } catch (e) {
    await fail(e, suspend ? '停權失敗' : '恢復失敗')
  } finally {
    statusBusy.value = false
  }
}

// ---------------------------------------------------------------------------
// dl 同步
// ---------------------------------------------------------------------------
const syncBusy = ref(false)
async function retrySync() {
  syncBusy.value = true
  try {
    const res = await pm.runSync()
    await load()
    if (res.status.feed.last_error) {
      await dialogs.notify({ title: '同步完成，但市集清單推送失敗', message: res.status.feed.last_error })
    }
  } catch (e) {
    await fail(e, '同步失敗')
  } finally {
    syncBusy.value = false
  }
}

// ---------------------------------------------------------------------------
// 上架／下架（營運主管以上）
// ---------------------------------------------------------------------------
const publishOpen = ref(false)
const publishTyped = ref('')
const publishBusy = ref(false)
const readyExists = computed(() => hasReadyVersion(plugin.value?.versions ?? []))
/** 上架按鈕能不能按（第三方要申請中；第一方照舊）。 */
const publishable = computed(() => (plugin.value ? canApproveListing(plugin.value, plugin.value.versions) : false))
function openPublish() {
  publishTyped.value = ''
  publishOpen.value = true
}
async function doPublish() {
  const p = plugin.value
  if (!p || !publishConfirmOk(publishTyped.value, p.key)) return
  publishBusy.value = true
  try {
    const res = await pm.publishPlugin(p.id)
    publishOpen.value = false
    await load()
    void refreshReviewBadge()
    if (!res.dl_synced) {
      await dialogs.notify({
        title: '已公開，但發布主機還沒同步',
        message: '已改成公開；發布主機那一半這次沒有完成，背景會在一分鐘內自動重試（也可以按〈重試同步〉）。',
      })
    }
  } catch (e) {
    await fail(e, '公開失敗')
  } finally {
    publishBusy.value = false
  }
}
async function doUnpublish() {
  const p = plugin.value
  if (!p) return
  const ok = await dialogs.confirm({
    title: `下架「${p.name}」`,
    message:
      '下架後改回私人：先從市集清單拿掉，再收回匿名下載。之後只有發行者自己的組織與被指派了權利的客戶看得到；' +
      '已安裝的站台照常運作，但沒有權利的站台收不到之後的更新。' +
      (thirdParty.value ? '第三方擴充下架後，上架申請會回到「未申請」，要再公開得由發行者重新申請。' : '') +
      '確定要下架嗎？',
    danger: true,
    confirmText: '下架',
  })
  if (!ok) return
  publishBusy.value = true
  try {
    const res = await pm.unpublishPlugin(p.id)
    await load()
    if (!res.dl_synced) {
      await dialogs.notify({
        title: '已下架，但發布主機還沒同步',
        message: '已從市集清單拿掉；收回匿名下載這一步這次沒有完成，背景會在一分鐘內自動重試。',
      })
    }
  } catch (e) {
    await fail(e, '下架失敗')
  } finally {
    publishBusy.value = false
  }
}
async function doRejectListing() {
  const p = plugin.value
  if (!p) return
  const note = await dialogs.prompt({
    title: `婉拒「${p.name}」的上架申請`,
    message: '婉拒後這個擴充維持私人；理由會顯示給發行者，他修正後可以再申請一次。',
    label: '婉拒理由（必填，發行者看得到）',
    placeholder: '例：說明文件缺少隱私權聲明',
    required: true,
    multiline: true,
    maxLength: 2000,
    danger: true,
    confirmText: '婉拒',
  })
  if (note === null) return
  publishBusy.value = true
  try {
    plugin.value = await pm.rejectListing(p.id, note)
    await load()
    void refreshReviewBadge()
  } catch (e) {
    await fail(e, '婉拒失敗')
  } finally {
    publishBusy.value = false
  }
}

// ---------------------------------------------------------------------------
// 版本：上傳、改說明、撤回
// ---------------------------------------------------------------------------
const pendingFile = ref<File | null>(null)
const uploadNotes = ref('')
const uploadBusy = ref(false)
const fileInput = ref<HTMLInputElement | null>(null)
/** 補充契約 C3：第三方 plugin 申請上架中＝上傳區停用（後端同樣 409 `listing_review_in_progress`）。 */
const uploadFrozen = computed(() => isUploadFrozen(plugin.value))
watch(uploadFrozen, (frozen) => {
  if (frozen) pendingFile.value = null
})
/** `onNoFile` 的提示（同 `lib/fileDrop.ts::NO_FILE_DROPPED_MESSAGE` 的理由，換成這裡的檔案種類）。 */
const NO_PLUGIN_FILE_MESSAGE =
  '拖進來的內容裡沒有可讀的檔案。從 Outlook、LINE 這類軟體直接拖附件時，瀏覽器拿不到檔案內容——' +
  '請先把 .ipoplug 存到電腦再拖進來，或點擊此區選擇檔案。'
// 凍結時 handler 照樣掛著（要擋掉瀏覽器「拖進來就直接開檔」的預設行為），只是不收檔
const { over, handlers: dropHandlers } = useFileDrop({
  onFile: (f) => {
    if (!uploadFrozen.value) pendingFile.value = f
  },
  onNoFile: () => {
    if (!uploadFrozen.value) void dialogs.notify({ title: '沒有收到檔案', message: NO_PLUGIN_FILE_MESSAGE })
  },
})
function chooseFile() {
  if (uploadFrozen.value) return
  fileInput.value?.click()
}
function onPick(ev: Event) {
  const f = (ev.target as HTMLInputElement).files?.[0]
  ;(ev.target as HTMLInputElement).value = ''
  if (f && !uploadFrozen.value) pendingFile.value = f
}
/**
 * 上傳（R3-1）：檔案先上傳、**說明之後另外以 JSON body 寫入**（說明放網址會在正式環境撞 414）。
 * 說明寫入失敗時檔案已經上去了——告訴使用者「版本已上傳，但說明未儲存」，讓他當場重試或之後再補。
 */
async function doUpload() {
  const p = plugin.value
  const f = pendingFile.value
  if (!p || !f || uploadBusy.value || uploadFrozen.value) return
  const notes = uploadNotes.value.trim()
  uploadBusy.value = true
  try {
    let v: MarketVersion
    try {
      v = await pm.uploadVersion(p.id, f)
    } catch (e) {
      await load()
      await fail(e, '上傳失敗')
      return
    }
    pendingFile.value = null
    uploadNotes.value = ''
    let notesMissing = false
    let notesDlError: string | null = null
    if (notes) {
      const saved = await saveNotesWithRetry(
        () => pm.updateVersionNotes(p.id, v.id, notes),
        (e) =>
          dialogs.confirm({
            title: '版本已上傳，但說明未儲存',
            message: `${v.version} 版已經上傳；寫入版本說明時失敗（${errText(e, '請稍後再試')}）。要再試一次嗎？`,
            confirmText: '重試',
            cancelText: '稍後再補',
          }),
      )
      if (saved === null) notesMissing = true
      else notesDlError = saved.dl_error
    }
    await load()
    const tail = notesMissing
      ? '\n\n版本說明沒有儲存：可以在版本列按〈說明〉補上。'
      : notesDlError
        ? `\n\n版本說明已儲存，但未同步到發布主機（${notesDlError}）；可以在版本列按〈說明〉再存一次。`
        : ''
    if (isPendingReview(v)) {
      void refreshReviewBadge()
      await dialogs.notify({
        title: '已上傳，等待審核',
        message: `${v.version} 版已送進審核佇列：核准之後才會出現在市集（〈市集 › 審核〉）。${tail}`,
      })
    } else {
      await dialogs.notify({ title: '已上架', message: `${v.version} 版已上傳到發布主機。${tail}` })
    }
  } finally {
    uploadBusy.value = false
  }
}

async function editNotes(v: MarketVersion) {
  const p = plugin.value
  if (!p) return
  const notes = await dialogs.prompt({
    title: `${v.version} 版說明`,
    message: '版本說明（markdown）會同步到發布主機，CS 的市集與「有新版」提示會顯示它。',
    initial: v.release_notes ?? '',
    multiline: true,
    maxLength: 20000,
    confirmText: '儲存',
  })
  if (notes === null) return
  try {
    const saved = await pm.updateVersionNotes(p.id, v.id, notes)
    await load()
    if (saved.dl_error) await dialogs.notify({ title: '說明已儲存，但未同步到發布主機', message: saved.dl_error })
  } catch (e) {
    await fail(e, '儲存版本說明失敗')
  }
}

async function withdraw(v: MarketVersion) {
  const p = plugin.value
  if (!p || !canWithdraw(v)) return
  const reason = await dialogs.prompt({
    title: `撤回 ${v.version} 版`,
    message:
      '撤回後這一版會從發布主機刪除，CS 端不能再安裝（已安裝的照常運作並顯示警示）；這個版號之後也不能再用。',
    label: '撤回理由（會記進稽核）',
    placeholder: '例：發現資料遺失的錯誤',
    required: true,
    maxLength: 1000,
    danger: true,
    confirmText: '撤回',
  })
  if (reason === null) return
  try {
    await pm.withdrawVersion(p.id, v.id, reason)
    await load()
  } catch (e) {
    await fail(e, '撤回失敗')
  }
}

// 下載（批B #27）：fetch＋Authorization 標頭轉 blob，token 不進 URL。
const downloading = ref('')
async function downloadVersion(v: MarketVersion) {
  const p = plugin.value
  if (!p) return
  downloading.value = v.id
  try {
    const { blob, filename } = await pm.downloadVersion(p.id, v.id)
    saveBlobFile(blob, packageFilename(p.key, v.version, filename))
  } catch (e) {
    await fail(e, '下載失敗')
  } finally {
    downloading.value = ''
  }
}
/** 能下載的版本：已上架（含待審）——撤回、失敗、婉拒的在發布主機上已經刪了。 */
function canDownload(v: MarketVersion): boolean {
  return v.status === 'ready' && !v.dl_deleted_at
}

// 待審版本的核准／婉拒（批B #28／#29，營運主管）。
const reviewBusy = ref('')
async function approveVersion(v: MarketVersion) {
  const p = plugin.value
  if (!p) return
  // R3-8：「核准處理中」的版本按〈重試核准〉＝後端原地重跑發布主機那一半（不重記稽核、審核者不變），不必再確認一次。
  if (!isApproving(v)) {
    const ok = await dialogs.confirm({
      title: `核准 ${v.version} 版`,
      message:
        '核准後這一版立刻發布到市集：任何站台都能看到並匿名下載，已安裝的站台會收到「有新版」提示。' +
        '請確認已經下載並檢查過這個包。',
      confirmText: '核准發布',
    })
    if (!ok) return
  }
  reviewBusy.value = v.id
  try {
    const res = await pm.approveVersion(p.id, v.id)
    await load()
    void refreshReviewBadge()
    if (!res.dl_synced) {
      await dialogs.notify({
        title: '已核准，發布主機還在處理',
        message: APPROVAL_PENDING_TEXT,
      })
    }
  } catch (e) {
    await fail(e, '核准失敗')
  } finally {
    reviewBusy.value = ''
  }
}
async function rejectVersion(v: MarketVersion) {
  const p = plugin.value
  if (!p) return
  const note = await dialogs.prompt({
    title: `婉拒 ${v.version} 版`,
    message: '婉拒後這一版不會發布；發行者修正後可以用同一個版號重新上傳。',
    label: '婉拒理由（必填，發行者看得到）',
    required: true,
    multiline: true,
    maxLength: 2000,
    danger: true,
    confirmText: '婉拒',
  })
  if (note === null) return
  reviewBusy.value = v.id
  try {
    await pm.rejectVersion(p.id, v.id, note)
    await load()
    void refreshReviewBadge()
  } catch (e) {
    await fail(e, '婉拒失敗')
  } finally {
    reviewBusy.value = ''
  }
}

// ---------------------------------------------------------------------------
// 指派碼（批B #21～#23，admin）
// ---------------------------------------------------------------------------
const codes = ref<GrantCode[]>([])
const codeCursor = ref<string | null>(null)
const codesBusy = ref(false)
/** 清單讀取失敗改成頁內錯誤列（R3-3）——不用 `await notify`：兩份清單同時失敗時會疊對話框、畫面卡在「載入中」。 */
const codesError = ref('')
let codesSeq = 0
async function loadCodes(reset: boolean) {
  if (!id.value) return
  if (!reset && (codesBusy.value || !codeCursor.value)) return
  const my = ++codesSeq
  const pid = id.value
  codesBusy.value = true
  try {
    const page = await pm.listGrantCodes(pid, { cursor: reset ? null : codeCursor.value, limit: 50 })
    if (my !== codesSeq || pid !== id.value) return
    codes.value = reset ? page.items : [...codes.value, ...page.items]
    codeCursor.value = page.next_cursor
    codesError.value = ''
  } catch (e) {
    if (my !== codesSeq || pid !== id.value) return
    codesError.value = errText(e, '讀取指派碼失敗')
  } finally {
    if (my === codesSeq) codesBusy.value = false
  }
}
/** 公開中的 plugin 不能用指派碼（兌換一律回無效）——發碼按鈕反灰並說明。 */
const codesBlocked = computed(() => plugin.value?.visibility === 'public' || plugin.value?.status !== 'active')

const issueOpen = ref(false)
const issueBusy = ref(false)
const issueError = ref('')
const issueForm = ref(emptyGrantCodeForm())
const issued = ref<IssuedGrantCode[]>([])
const issuedCopied = ref(false)
const dateBounds = ref(grantCodeDateBounds())
const issueInvalid = computed(() => grantCodeFormError(issueForm.value))
function openIssue() {
  issueForm.value = emptyGrantCodeForm()
  dateBounds.value = grantCodeDateBounds()
  issued.value = []
  issuedCopied.value = false
  issueError.value = ''
  issueOpen.value = true
}
async function doIssue() {
  const p = plugin.value
  if (!p || issueInvalid.value || issueBusy.value) return
  issueBusy.value = true
  issueError.value = ''
  try {
    const res = await pm.issueGrantCodes(p.id, grantCodeBody(issueForm.value))
    issued.value = res.items
    await loadCodes(true)
  } catch (e) {
    issueError.value = apiErrorText(e, GRANT_CODE_ERROR_TEXT, '發碼失敗')
  } finally {
    issueBusy.value = false
  }
}
async function copyIssued() {
  const text = issuedCodesText(issued.value)
  if (await copyText(text)) {
    issuedCopied.value = true
    await dialogs.notify({ title: '已複製', message: `已複製 ${issued.value.length} 組指派碼。` })
  } else {
    await dialogs.notify({ title: '無法存取剪貼簿', message: '請直接在清單裡選取文字手動複製。' })
  }
}
/** 明碼顯示中、還沒按過複製就要關——多問一次（關掉就再也看不到了）。 */
async function closeIssue() {
  if (issued.value.length && !issuedCopied.value) {
    const ok = await dialogs.confirm({
      title: '還沒複製指派碼',
      message: '關閉之後就再也看不到這些明碼了（只會留末四碼）。確定要關閉嗎？',
      danger: true,
      confirmText: '關閉',
    })
    if (!ok) return
  }
  issueOpen.value = false
  issued.value = []
}

/**
 * 還有沒複製的明碼就要離開這一頁（與 portal 的 R3-19 同一條規則）：多問一次——離開之後就再也看不到了。
 * 換到另一支 plugin（同一個元件、只換 `:id`）也算離開。
 */
async function confirmLeaveIssued(): Promise<boolean> {
  if (!issued.value.length || issuedCopied.value) return true
  const ok = await dialogs.confirm({
    title: '還沒複製指派碼',
    message: '離開這一頁之後就再也看不到這些明碼了（只會留末四碼）。確定要離開嗎？',
    danger: true,
    confirmText: '離開',
    cancelText: '留在這裡',
  })
  if (ok) {
    issueOpen.value = false
    issued.value = []
  }
  return ok
}
onBeforeRouteLeave(confirmLeaveIssued)
onBeforeRouteUpdate(confirmLeaveIssued)

async function voidCode(c: GrantCode) {
  const ok = await dialogs.confirm({
    title: '作廢指派碼',
    message: `作廢 ${maskedGrantCode(c.code_hint)}？作廢後這組碼永遠不能兌換；已經用這組碼取得的權利不受影響。`,
    danger: true,
    confirmText: '作廢',
  })
  if (!ok) return
  try {
    await pm.voidGrantCode(c.id)
    await loadCodes(true)
  } catch (e) {
    await fail(e, '作廢失敗')
  }
}

// ---------------------------------------------------------------------------
// 權利
// ---------------------------------------------------------------------------
const grants = ref<PluginGrant[]>([])
const grantCursor = ref<string | null>(null)
const grantsBusy = ref(false)
/** 同 `codesError`（R3-3）：頁內錯誤列。 */
const grantsError = ref('')
let grantsSeq = 0
async function loadGrants(reset: boolean) {
  if (!id.value) return
  if (!reset && (grantsBusy.value || !grantCursor.value)) return
  const my = ++grantsSeq
  const pid = id.value
  grantsBusy.value = true
  try {
    const page = await pm.listGrants({ pluginId: pid, cursor: reset ? null : grantCursor.value, limit: 50 })
    if (my !== grantsSeq || pid !== id.value) return
    grants.value = reset ? page.items : [...grants.value, ...page.items]
    grantCursor.value = page.next_cursor
    grantsError.value = ''
  } catch (e) {
    if (my !== grantsSeq || pid !== id.value) return
    grantsError.value = errText(e, '讀取權利失敗')
  } finally {
    if (my === grantsSeq) grantsBusy.value = false
  }
}

const assignOpen = ref(false)
const assignBusy = ref(false)
const assign = ref({ customerId: '', expires: '', note: '' })
/** 平台（SaaS）客戶不使用市集，不列成指派候選（後端 409 `platform_managed_customer`）。 */
const assignCustomers = computed(() => store.customers.filter(isMarketCandidate))
function openAssign() {
  assign.value = { customerId: '', expires: '', note: '' }
  assignOpen.value = true
}
async function doAssign() {
  const p = plugin.value
  if (!p || !assign.value.customerId) return
  assignBusy.value = true
  try {
    await pm.upsertGrant({
      plugin_id: p.id,
      customer_id: assign.value.customerId,
      expires_at: dateToExpiry(assign.value.expires),
      note: assign.value.note.trim() || null,
    })
    assignOpen.value = false
    await Promise.all([loadGrants(true), load()])
  } catch (e) {
    await fail(e, '指派失敗')
  } finally {
    assignBusy.value = false
  }
}
async function revoke(g: PluginGrant) {
  const reason = await dialogs.prompt({
    title: `撤銷「${g.customer_name ?? g.customer_id}」的權利`,
    message: '撤銷後這個客戶列不出、下載不到、也升級不了這個擴充；已安裝的照常運作。',
    label: '撤銷理由（選填，會記進稽核）',
    maxLength: 1000,
    danger: true,
    confirmText: '撤銷',
  })
  if (reason === null) return
  try {
    await pm.revokeGrant(g.id, reason)
    await Promise.all([loadGrants(true), load()])
  } catch (e) {
    await fail(e, '撤銷失敗')
  }
}

function back() {
  void router.push({ name: 'plugins' })
}
</script>

<template>
  <div class="page">
    <div class="page-head">
      <div class="title">
        <button class="back" type="button" @click="back()">← 擴充</button>
        <h1>{{ plugin?.name ?? '擴充' }}</h1>
        <template v-if="plugin">
          <span class="status-pill" :class="plugin.visibility === 'public' ? 'active' : 'superseded'">
            {{ labelOf(VISIBILITY_LABELS, plugin.visibility) }}
          </span>
          <span v-if="plugin.status !== 'active'" class="status-pill revoked">
            {{ labelOf(PLUGIN_STATUS_LABELS, plugin.status) }}
          </span>
        </template>
      </div>
    </div>

    <div v-if="loading && !plugin" class="loading-wrap"><span class="spinner lg" /></div>
    <p v-else-if="loadError" class="err">{{ loadError }}</p>

    <template v-else-if="plugin">
      <div class="cols">
        <!-- 資訊卡 -->
        <div class="card">
          <div class="card-head">
            <h3 class="section-title">資訊</h3>
            <span class="spacer" />
            <UiButton v-if="isManager" size="sm" variant="secondary" :loading="statusBusy" @click="toggleStatus">
              {{ plugin.status === 'suspended' ? '恢復' : '停權…' }}
            </UiButton>
            <UiButton size="sm" variant="secondary" @click="openEdit">編輯</UiButton>
          </div>
          <div class="info-head">
            <img v-if="plugin.icon_data_uri" :src="plugin.icon_data_uri" alt="" class="icon" />
            <div v-else class="icon ph" aria-hidden="true">{{ plugin.name.slice(0, 1) }}</div>
            <div>
              <div class="pname">{{ plugin.name }}</div>
              <div class="subtle">{{ plugin.summary || '（沒有一句話說明）' }}</div>
            </div>
          </div>
          <dl class="kv">
            <dt>擴充代號</dt><dd><code class="mono">{{ plugin.key }}</code></dd>
            <dt>產品</dt><dd>{{ store.productName(plugin.product) }}</dd>
            <dt>發行者</dt>
            <dd>
              {{ plugin.publisher_name ?? '—' }}
              <span v-if="plugin.first_party" class="chip">第一方</span>
            </dd>
            <dt>最新版</dt><dd><code class="mono">{{ plugin.latest_version ?? '—' }}</code></dd>
            <dt>有效權利</dt><dd>{{ plugin.active_grants }}</dd>
            <dt>首頁</dt>
            <dd>
              <a v-if="safeHttpUrl(plugin.homepage_url)" :href="safeHttpUrl(plugin.homepage_url) ?? undefined" target="_blank" rel="noopener noreferrer">
                {{ plugin.homepage_url }}
              </a>
              <span v-else-if="plugin.homepage_url">{{ plugin.homepage_url }}</span>
              <span v-else class="subtle">—</span>
            </dd>
            <dt>市集排序</dt><dd>{{ plugin.sort_order }}</dd>
            <dt>登記</dt><dd class="subtle">{{ fmtSeen(plugin.created_at) }}・{{ plugin.created_by ?? '—' }}</dd>
          </dl>
          <details v-if="plugin.description_md" class="desc">
            <summary>說明（markdown 原文）</summary>
            <pre>{{ plugin.description_md }}</pre>
          </details>
        </div>

        <div class="side">
          <!-- dl 同步 -->
          <div class="card">
            <div class="card-head">
              <h3 class="section-title">發布主機同步</h3>
              <span class="spacer" />
              <UiButton size="sm" variant="secondary" :loading="syncBusy" @click="retrySync">重試同步</UiButton>
            </div>
            <div class="sync-row">
              <span :class="['sync', sync]">● {{ DL_SYNC_LABELS[sync] }}</span>
              <span class="subtle">
                目前 {{ plugin.dl_access ? labelOf(VISIBILITY_LABELS, plugin.dl_access) : '—' }}
                ／應為 {{ plugin.desired_dl_access ? labelOf(VISIBILITY_LABELS, plugin.desired_dl_access) : '—' }}
              </span>
            </div>
            <dl class="kv tight">
              <dt>channel</dt><dd><code class="mono small">{{ plugin.dl_channel }}</code></dd>
              <dt>上次同步</dt><dd class="subtle">{{ fmtSeen(plugin.dl_synced_at) }}</dd>
            </dl>
            <p v-if="plugin.dl_error" class="err">{{ plugin.dl_error }}</p>
            <p v-if="sync === 'pending'" class="subtle small">
              上架／下架的發布主機那一半還沒完成，背景每分鐘會自動重試。
            </p>
          </div>

          <!-- 上架 -->
          <div class="card">
            <div class="card-head"><h3 class="section-title">市集上架</h3></div>
            <p class="subtle small">
              <template v-if="plugin.visibility === 'public'">
                公開中：任何站台都能在市集看到、匿名下載每一個已上架的版本。
              </template>
              <template v-else>
                私人：只有發行者自己的組織與被指派了權利的客戶看得到。
              </template>
            </p>
            <!-- 公開過又改回私人：撤回仍經公開市集通知曾經從市集安裝過它的站（feed 的 withdrawn）。 -->
            <p v-if="plugin.visibility !== 'public' && plugin.first_published_at" class="subtle small">
              曾於 {{ fmtSeen(plugin.first_published_at, { short: true }) }} 公開過：之後撤回的版本仍會透過市集通知曾經安裝過它的站。
            </p>
            <!-- 批B：第三方的上架申請狀態（第一方不審，不顯示這一列）。 -->
            <div v-if="thirdParty" class="listing-row">
              <span class="subtle small">上架申請</span>
              <span class="status-pill" :class="LISTING_STATUS_PILL[plugin.listing_status] ?? ''">
                {{ labelOf(LISTING_STATUS_LABELS, plugin.listing_status) }}
              </span>
              <span v-if="plugin.listing_status === 'pending' && plugin.listing_requested_at" class="subtle small">
                {{ fmtSeen(plugin.listing_requested_at, { short: true }) }}・{{ plugin.listing_requested_by ?? '—' }}
              </span>
            </div>
            <p v-if="thirdParty && plugin.listing_status === 'rejected' && plugin.listing_note" class="reject-note">
              婉拒理由：{{ plugin.listing_note }}
            </p>
            <p v-if="reviewedUploads" class="subtle small">
              第三方的公開擴充：之後上傳的每一版都要經營運主管審核，核准後才會出現在市集。
            </p>
            <div v-if="showPublish" class="acts">
              <UiButton v-if="plugin.visibility === 'public'" variant="warn" :loading="publishBusy" @click="doUnpublish">
                下架改回私人…
              </UiButton>
              <template v-else-if="thirdParty">
                <UiButton variant="primary" :disabled="!publishable" @click="openPublish">核准上架…</UiButton>
                <UiButton
                  variant="danger"
                  :disabled="plugin.listing_status !== 'pending'"
                  :loading="publishBusy"
                  @click="doRejectListing"
                >
                  婉拒…
                </UiButton>
                <small v-if="plugin.listing_status !== 'pending'" class="subtle">
                  第三方擴充要等發行者在客戶 portal 申請公開上架，才能核准或婉拒。
                </small>
                <small v-else-if="!readyExists" class="subtle">至少要有一個已上架的版本才能公開。</small>
              </template>
              <template v-else>
                <UiButton variant="primary" :disabled="!publishable" @click="openPublish">公開到市集…</UiButton>
                <small v-if="!readyExists" class="subtle">至少要有一個已上架的版本才能公開。</small>
              </template>
            </div>
            <p v-else class="subtle small">上架與下架需要營運主管以上的權限。</p>
          </div>
        </div>
      </div>

      <!-- 版本 -->
      <div class="card">
        <div class="card-head"><h3 class="section-title">版本</h3></div>
        <div
          class="drop"
          :class="{ over: over && !uploadFrozen, frozen: uploadFrozen }"
          role="button"
          :tabindex="uploadFrozen ? -1 : 0"
          :aria-disabled="uploadFrozen ? 'true' : undefined"
          data-testid="upload-drop"
          v-on="dropHandlers"
          @click="chooseFile"
          @keydown.enter.prevent="chooseFile"
        >
          <input ref="fileInput" type="file" accept=".ipoplug" hidden @change="onPick" />
          <template v-if="pendingFile">
            <strong>{{ pendingFile.name }}</strong>
            <span class="subtle">（{{ formatBytes(pendingFile.size) }}）— 點這裡換一個檔案</span>
          </template>
          <!-- 補充契約 C3：兩層疊在同一格、只切換顯示——拖放區的大小不隨凍結與否改變（版本表不跳動） -->
          <div v-else class="drop-stack">
            <div class="drop-layer" :class="{ 'is-hidden': uploadFrozen }" :aria-hidden="uploadFrozen ? 'true' : undefined">
              <strong>拖放 <code>.ipoplug</code> 到這裡</strong>
              <span class="subtle">或點擊選擇檔案。包的 key 要等於「{{ plugin.key }}」、簽章公鑰要是發行者登錄的有效公鑰、版號要比現有的都大。</span>
              <span v-if="reviewedUploads" class="subtle">這是第三方的公開擴充：上傳後先進審核佇列，核准後才發布。</span>
            </div>
            <div class="drop-layer" :class="{ 'is-hidden': !uploadFrozen }" :aria-hidden="uploadFrozen ? undefined : 'true'" data-testid="upload-frozen-note">
              <strong>暫停上傳</strong>
              <span class="subtle">{{ LISTING_REVIEW_FREEZE_TEXT }}</span>
            </div>
          </div>
        </div>
        <div v-if="pendingFile" class="upload-row">
          <!-- R3-1：說明在上傳成功後另外寫入（JSON body），不放網址上，所以長度上限與〈說明〉相同。 -->
          <textarea
            v-model="uploadNotes"
            rows="3"
            maxlength="20000"
            placeholder="版本說明（選填，markdown；上傳成功後寫入）"
            :disabled="uploadBusy"
          />
          <div class="acts">
            <UiButton :disabled="uploadBusy" @click="pendingFile = null">取消</UiButton>
            <UiButton variant="primary" :loading="uploadBusy" @click="doUpload">上傳</UiButton>
          </div>
        </div>

        <div class="tbl-scroll" data-hscroll>
          <table v-card-table class="tbl card-table sticky-acts">
            <thead>
              <tr>
                <th>版號</th><th>狀態</th><th>大小</th><th>簽章指紋</th><th>CS 最低版</th><th>上架</th><th>說明</th><th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="v in plugin.versions" :key="v.id" :class="{ gone: v.status === 'withdrawn' }">
                <td class="nowrap"><code class="mono">{{ v.version }}</code></td>
                <td class="nowrap status-cell">
                  <span class="status-pill" :class="versionDisplay(v).pill">{{ versionDisplay(v).label }}</span>
                  <div v-if="v.dl_error && v.status !== 'withdrawn'" class="err small" :title="v.dl_error">
                    {{ v.dl_error.slice(0, 60) }}
                  </div>
                  <div v-if="v.withdraw_reason" class="subtle small">理由：{{ v.withdraw_reason }}</div>
                  <div v-if="v.review_status === 'rejected' && v.review_note" class="reject-note small wrap">
                    婉拒：{{ v.review_note }}
                  </div>
                  <div v-else-if="v.review_status === 'approved' && v.status !== 'withdrawn'" class="subtle small">
                    審核{{ labelOf(REVIEW_STATUS_LABELS, v.review_status) }}・{{ v.reviewed_by ?? '—' }}
                  </div>
                </td>
                <td class="nowrap subtle">{{ formatBytes(v.size) }}</td>
                <td class="nowrap">
                  <code class="mono small" :title="v.signer_fingerprint">{{ formatFingerprint(v.signer_fingerprint.slice(0, 16)) }}</code>
                </td>
                <td class="nowrap subtle">{{ v.min_cs_version ?? '—' }}</td>
                <td class="nowrap subtle">{{ fmtSeen(v.ready_at, { short: true }) }}</td>
                <td class="notes">{{ v.release_notes || '—' }}</td>
                <td class="acts-cell">
                  <div class="acts">
                    <!-- R3-8：核准處理中（等發布主機那一半）→ 同一顆按鈕變〈重試核准〉（後端原地重跑），婉拒反灰。 -->
                    <template v-if="isManager && (isPendingReview(v) || isApproving(v))">
                      <UiButton
                        size="sm"
                        variant="primary"
                        :loading="reviewBusy === v.id"
                        :title="isApproving(v) ? '發布主機那一半還沒完成；背景約 10 分鐘內會自動接手，也可以手動重試' : undefined"
                        @click="approveVersion(v)"
                      >
                        {{ isApproving(v) ? '重試核准' : '核准…' }}
                      </UiButton>
                      <UiButton
                        size="sm"
                        variant="danger"
                        :disabled="reviewBusy === v.id || isApproving(v)"
                        @click="rejectVersion(v)"
                      >
                        婉拒…
                      </UiButton>
                    </template>
                    <UiButton
                      v-if="canDownload(v)"
                      size="sm"
                      variant="secondary"
                      :loading="downloading === v.id"
                      @click="downloadVersion(v)"
                    >
                      下載
                    </UiButton>
                    <UiButton v-if="v.status !== 'withdrawn'" size="sm" variant="secondary" @click="editNotes(v)">說明</UiButton>
                    <!-- R1-3：核准處理中的版本後端回 409 review_in_progress——按鈕留著但反灰，位置不跳動。 -->
                    <UiButton
                      v-if="canWithdraw(v) || isApproving(v)"
                      size="sm"
                      variant="danger"
                      :disabled="isApproving(v)"
                      :title="isApproving(v) ? WITHDRAW_BLOCKED_APPROVING : undefined"
                      @click="withdraw(v)"
                    >
                      撤回…
                    </UiButton>
                  </div>
                </td>
              </tr>
              <tr v-if="!plugin.versions.length">
                <td colspan="8" class="subtle empty-row">還沒有任何版本。</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- 指派碼（批B） -->
      <div class="card">
        <div class="card-head">
          <h3 class="section-title">指派碼</h3>
          <span class="spacer" />
          <UiButton size="sm" variant="secondary" :disabled="codesBlocked" @click="openIssue">＋ 發指派碼</UiButton>
        </div>
        <p class="subtle small">
          一次性的碼：客戶在產品的市集或客戶 portal 輸入後，就取得這個擴充的權利（綁在他的組織上）。
          明碼只在發出的當下顯示一次，清單只留末四碼。
          <template v-if="plugin.visibility === 'public'">（這支目前是公開的，指派碼只用在私人擴充。）</template>
          <template v-else-if="plugin.status !== 'active'">（這支已停權，不能發碼。）</template>
        </p>
        <div class="tbl-scroll" data-hscroll>
          <table v-card-table class="tbl card-table sticky-acts">
            <thead>
              <tr><th>碼</th><th>狀態</th><th>碼到期</th><th>權利期限</th><th>兌換</th><th>備註</th><th>發碼</th><th></th></tr>
            </thead>
            <tbody>
              <tr
                v-for="c in codes"
                :key="c.id"
                :class="{ gone: grantCodeDisplay(c, plugin.visibility).state !== 'issued' }"
              >
                <td class="nowrap"><code class="mono">{{ maskedGrantCode(c.code_hint) }}</code></td>
                <td class="nowrap">
                  <!-- 公開中的 plugin：未使用的碼一律兌換不了 → 「公開中無法使用」（中性灰），作廢照樣可按。 -->
                  <span class="status-pill" :class="grantCodeDisplay(c, plugin.visibility).pill">
                    {{ grantCodeDisplay(c, plugin.visibility).label }}
                  </span>
                </td>
                <td class="nowrap subtle">{{ fmtSeen(c.expires_at, { short: true }) }}</td>
                <td class="nowrap subtle">{{ grantTermLabel(c.grant_term_days) }}</td>
                <td class="nowrap subtle">
                  <template v-if="c.redeemed_at">
                    {{ fmtSeen(c.redeemed_at, { short: true }) }}
                    <div class="small">{{ c.redeemed_customer_name ?? c.redeemed_customer_id ?? '—' }}</div>
                  </template>
                  <template v-else>—</template>
                </td>
                <td class="wide">{{ c.note ?? '—' }}</td>
                <td class="nowrap subtle">
                  {{ fmtSeen(c.created_at, { short: true }) }}
                  <div class="small">{{ c.issued_realm === 'portal' ? '發行者' : (c.issued_by ?? '—') }}</div>
                </td>
                <td class="acts-cell">
                  <UiButton v-if="canVoidGrantCode(c)" size="sm" variant="danger" @click="voidCode(c)">作廢…</UiButton>
                </td>
              </tr>
              <tr v-if="!codesBusy && !codesError && !codes.length">
                <td colspan="8" class="subtle empty-row">還沒有發過指派碼。</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-if="codesError" class="err list-err" data-testid="codes-error">
          {{ codesError }}
          <UiButton size="sm" @click="loadCodes(true)">重新讀取</UiButton>
        </p>
        <div class="more-row">
          <span v-if="codesBusy" class="subtle"><span class="spinner" /> 載入中…</span>
          <UiButton v-else-if="codeCursor" size="sm" @click="loadCodes(false)">載入更多</UiButton>
        </div>
      </div>

      <!-- 權利 -->
      <div class="card">
        <div class="card-head">
          <h3 class="section-title">權利</h3>
          <span class="spacer" />
          <UiButton size="sm" variant="secondary" @click="openAssign">＋ 指派客戶</UiButton>
        </div>
        <p class="subtle small">
          發行者自己的組織天生有權，不必指派。權利撤銷或到期只停止列出、下載、升級——已安裝的照常運作。
          <template v-if="plugin.visibility === 'public'">（這支目前是公開的，權利要等它改回私人才有作用。）</template>
        </p>
        <div class="tbl-scroll" data-hscroll>
          <table v-card-table class="tbl card-table sticky-acts">
            <thead>
              <tr><th>客戶</th><th>狀態</th><th>到期</th><th>來源</th><th>備註</th><th>指派</th><th></th></tr>
            </thead>
            <tbody>
              <tr v-for="g in grants" :key="g.id" :class="{ gone: grantState(g) !== 'effective' }">
                <td class="wide">{{ g.customer_name ?? g.customer_id }}</td>
                <td class="nowrap">
                  <span class="status-pill" :class="GRANT_STATE_PILL[grantState(g)]">{{ GRANT_STATE_LABELS[grantState(g)] }}</span>
                </td>
                <td class="nowrap subtle">{{ g.expires_at ? fmtSeen(g.expires_at, { short: true }) : '不到期' }}</td>
                <td class="nowrap">{{ labelOf(GRANT_SOURCE_LABELS, g.source) }}</td>
                <td class="wide">{{ g.note ?? '—' }}</td>
                <td class="nowrap subtle">{{ fmtSeen(g.created_at, { short: true }) }}</td>
                <td class="acts-cell">
                  <UiButton v-if="g.status === 'active'" size="sm" variant="danger" @click="revoke(g)">撤銷…</UiButton>
                </td>
              </tr>
              <tr v-if="!grantsBusy && !grantsError && !grants.length">
                <td colspan="7" class="subtle empty-row">還沒有指派給任何客戶。</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-if="grantsError" class="err list-err" data-testid="grants-error">
          {{ grantsError }}
          <UiButton size="sm" @click="loadGrants(true)">重新讀取</UiButton>
        </p>
        <div class="more-row">
          <span v-if="grantsBusy" class="subtle"><span class="spinner" /> 載入中…</span>
          <UiButton v-else-if="grantCursor" size="sm" @click="loadGrants(false)">載入更多</UiButton>
        </div>
      </div>
    </template>

    <!-- 編輯呈現欄位 -->
    <UiModal :open="editOpen" title="編輯擴充" width="640px" :close-on-backdrop="false" @close="editOpen = false">
      <div class="form-grid two">
        <label class="field">
          <span class="field-label">名稱</span>
          <input v-model="edit.name" maxlength="60" />
        </label>
        <label class="field">
          <span class="field-label">市集排序（小的在前）</span>
          <input v-model="edit.sortOrder" inputmode="numeric" />
        </label>
      </div>
      <label class="field block">
        <span class="field-label">一句話說明（≤160 字）</span>
        <input v-model="edit.summary" maxlength="160" />
      </label>
      <label class="field block">
        <span class="field-label">說明（markdown，≤20000 字）</span>
        <textarea v-model="edit.description" rows="6" maxlength="20000" />
      </label>
      <label class="field block">
        <span class="field-label">首頁網址</span>
        <input v-model="edit.homepage" type="url" placeholder="https://…" maxlength="500" />
      </label>
      <div class="field block">
        <span class="field-label">圖示（png／webp／jpeg，16 KB 以內）</span>
        <div class="icon-row">
          <img v-if="edit.icon" :src="edit.icon" alt="" class="icon" />
          <label class="btn ghost sm">
            選擇圖片…
            <input type="file" accept="image/png,image/webp,image/jpeg" hidden @change="pickIcon" />
          </label>
          <UiButton v-if="edit.icon" size="sm" @click="edit.icon = null">移除</UiButton>
        </div>
      </div>
      <p v-if="editInvalid" class="subtle">{{ editInvalid }}</p>
      <template #foot>
        <UiButton @click="editOpen = false">取消</UiButton>
        <UiButton variant="primary" :disabled="!!editInvalid" :loading="editBusy" @click="saveEdit">儲存</UiButton>
      </template>
    </UiModal>

    <!-- 公開（打字確認）；第三方＝核准上架申請 -->
    <UiModal
      :open="publishOpen"
      :title="thirdParty ? '核准上架申請' : '公開到市集'"
      width="540px"
      @close="publishOpen = false"
    >
      <template v-if="plugin">
        <ul class="warn-list">
          <li>公開＝<strong>對全世界發布</strong>：任何站台都能在市集看到「{{ plugin.name }}」，並匿名下載每一個已上架的版本。</li>
          <li v-if="thirdParty">發行者是第三方：之後上傳的每一版都會先進審核佇列，核准後才出現在市集。</li>
          <li v-else>之後上傳的新版本會直接出現在市集。</li>
          <li>要收回請按〈下架改回私人〉（已經下載的人手上的檔案收不回來）。</li>
        </ul>
        <label class="field">
          <span class="field-label">請輸入 <code class="mono">{{ plugin.key }}</code> 確認</span>
          <input v-model="publishTyped" class="mono" placeholder="擴充代號" v-enter="doPublish" />
        </label>
      </template>
      <template #foot>
        <UiButton @click="publishOpen = false">取消</UiButton>
        <UiButton
          variant="primary"
          :loading="publishBusy"
          :disabled="!plugin || !publishConfirmOk(publishTyped, plugin.key)"
          @click="doPublish"
        >
          {{ thirdParty ? '核准並公開' : '公開' }}
        </UiButton>
      </template>
    </UiModal>

    <!-- 發指派碼（明碼只顯示一次） -->
    <UiModal
      :open="issueOpen"
      :title="issued.length ? '指派碼已發出' : '發指派碼'"
      width="560px"
      :close-on-backdrop="false"
      @close="closeIssue"
    >
      <template v-if="!issued.length">
        <p class="subtle">每一組各自獨立、只能兌換一次。兌換的組織已經有有效權利時不會消耗碼。</p>
        <p v-if="issueError" class="err">{{ issueError }}</p>
        <div class="form-grid two">
          <label class="field">
            <span class="field-label">碼的到期日（必填）</span>
            <input v-model="issueForm.expires" type="date" :min="dateBounds.min" :max="dateBounds.max" />
            <small class="subtle">明天到 365 天後；過期沒兌換就失效。</small>
          </label>
          <label class="field">
            <span class="field-label">權利天數（選填）</span>
            <input v-model="issueForm.termDays" inputmode="numeric" placeholder="留空＝兌換後不到期" maxlength="5" />
            <small class="subtle">兌換之後權利多少天到期。</small>
          </label>
          <label class="field">
            <span class="field-label">份數（1–{{ GRANT_CODE_MAX_COUNT }}）</span>
            <input v-model="issueForm.count" inputmode="numeric" maxlength="2" v-enter="doIssue" />
          </label>
          <label class="field">
            <span class="field-label">備註（選填，≤{{ GRANT_CODE_NOTE_MAX }} 字）</span>
            <input v-model="issueForm.note" :maxlength="GRANT_CODE_NOTE_MAX" placeholder="例：合約編號" v-enter="doIssue" />
          </label>
        </div>
        <p v-if="issueInvalid && issueForm.expires" class="subtle">{{ issueInvalid }}</p>
      </template>
      <template v-else>
        <p class="once-warn">
          這是<strong>唯一一次</strong>看得到明碼的機會——關閉後就看不到了（清單只留末四碼）。請先複製再關閉。
        </p>
        <ul class="issued-list">
          <li v-for="c in issued" :key="c.id"><code class="mono code">{{ c.code }}</code></li>
        </ul>
        <p class="subtle small">
          {{ grantTermLabel(issued[0]?.grant_term_days) }}・碼於 {{ fmtSeen(issued[0]?.expires_at, { short: true }) }} 到期
        </p>
      </template>
      <template #foot>
        <template v-if="!issued.length">
          <UiButton @click="closeIssue">取消</UiButton>
          <UiButton variant="primary" :disabled="!!issueInvalid" :loading="issueBusy" @click="doIssue">發碼</UiButton>
        </template>
        <template v-else>
          <UiButton @click="closeIssue">關閉</UiButton>
          <UiButton variant="primary" @click="copyIssued">全部複製</UiButton>
        </template>
      </template>
    </UiModal>

    <!-- 指派客戶 -->
    <UiModal :open="assignOpen" title="指派給客戶" width="560px" @close="assignOpen = false">
      <p class="subtle">已經有有效權利的客戶再指派一次＝就地更新到期日與備註。</p>
      <div class="form-grid">
        <label class="field">
          <span class="field-label">客戶</span>
          <select v-model="assign.customerId" data-testid="assign-customer">
            <option value="">（請選擇）</option>
            <option v-for="c in assignCustomers" :key="c.id" :value="c.id">{{ customerOptionLabel(c) }}</option>
          </select>
        </label>
        <label class="field">
          <span class="field-label">到期日（留空＝不到期）</span>
          <input v-model="assign.expires" type="date" />
        </label>
        <label class="field">
          <span class="field-label">備註（選填）</span>
          <input v-model="assign.note" maxlength="2000" placeholder="例：合約編號" />
        </label>
      </div>
      <template #foot>
        <UiButton @click="assignOpen = false">取消</UiButton>
        <UiButton variant="primary" :disabled="!assign.customerId" :loading="assignBusy" @click="doAssign">指派</UiButton>
      </template>
    </UiModal>
  </div>
</template>

<style scoped>
.page-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-3);
  margin-bottom: var(--sp-4);
}
.title {
  display: flex;
  align-items: center;
  gap: var(--sp-3);
  flex-wrap: wrap;
}
.title h1 {
  margin: 0;
  font-size: var(--fs-20);
  color: var(--fg);
}
.back {
  border: none;
  background: none;
  color: var(--fg-3);
  font-family: inherit;
  font-size: var(--fs-13);
  cursor: pointer;
  padding: 0;
}
.back:hover {
  color: var(--brand-600);
}
.spacer {
  flex: 1;
}
.err {
  margin: var(--sp-2) 0;
  color: var(--danger);
  font-size: var(--fs-12);
}
.cols {
  display: grid;
  grid-template-columns: minmax(0, 3fr) minmax(0, 2fr);
  gap: var(--sp-4);
  align-items: start;
  margin-bottom: var(--sp-4);
}
.cols .card {
  min-width: 0;
}
.side {
  display: grid;
  gap: var(--sp-4);
  min-width: 0;
}
.side .card + .card {
  margin-top: 0;
}
.info-head {
  display: flex;
  gap: var(--sp-3);
  align-items: center;
  margin: var(--sp-2) 0 var(--sp-3);
}
.icon {
  width: 48px;
  height: 48px;
  border-radius: var(--r-md);
  object-fit: cover;
  flex: none;
}
.icon.ph {
  display: grid;
  place-items: center;
  background: var(--brand-100);
  color: var(--brand-700);
  font-weight: 700;
  font-size: var(--fs-17);
}
.pname {
  font-weight: 600;
  font-size: var(--fs-15);
  color: var(--fg);
}
.kv {
  display: grid;
  grid-template-columns: 96px minmax(0, 1fr);
  gap: 6px var(--sp-3);
  margin: 0;
  font-size: var(--fs-13);
}
.kv dt {
  color: var(--fg-3);
}
.kv dd {
  margin: 0;
  min-width: 0;
  overflow-wrap: anywhere;
}
.kv.tight {
  margin-top: var(--sp-2);
}
.desc {
  margin-top: var(--sp-3);
  font-size: var(--fs-13);
}
.desc pre {
  white-space: pre-wrap;
  font-family: var(--font-mono);
  font-size: var(--fs-12);
  background: var(--bg);
  padding: var(--sp-2);
  border-radius: var(--r-sm);
  max-height: 240px;
  overflow: auto;
}
.sync-row {
  display: flex;
  gap: var(--sp-3);
  align-items: center;
  flex-wrap: wrap;
  margin-top: var(--sp-2);
}
.sync {
  font-size: var(--fs-13);
  font-weight: 600;
}
.sync.synced {
  color: var(--success);
}
.sync.pending {
  color: var(--warning);
}
.sync.error {
  color: var(--danger);
}
.sync.none {
  color: var(--fg-4);
}
.acts {
  display: flex;
  gap: var(--sp-2);
  align-items: center;
  flex-wrap: wrap;
}
.drop {
  display: grid;
  gap: 4px;
  justify-items: center;
  text-align: center;
  padding: var(--sp-5) var(--sp-4);
  margin: var(--sp-2) 0 var(--sp-3);
  border: 2px dashed var(--border-strong);
  border-radius: var(--r-md);
  background: var(--bg);
  cursor: pointer;
  font-size: var(--fs-13);
  transition: border-color var(--dur-fast) var(--ease), background var(--dur-fast) var(--ease);
}
.drop:hover,
.drop.over {
  border-color: var(--brand-500);
  background: var(--brand-50);
}
[data-theme='dark'] .drop.over {
  background: var(--bg-active);
}
/* 補充契約 C3：申請上架中＝停用（游標、框線、底色換成停用樣式；說明在拖放區裡的固定位置） */
.drop.frozen,
.drop.frozen:hover {
  cursor: not-allowed;
  border-color: var(--border);
  background: var(--bg-sunken);
}
.drop.frozen strong {
  color: var(--fg-3);
}
.drop-stack {
  display: grid;
  width: 100%;
}
.drop-layer {
  grid-area: 1 / 1;
  display: grid;
  gap: 4px;
  justify-items: center;
}
.drop-layer.is-hidden {
  visibility: hidden;
}
.upload-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: var(--sp-3);
  align-items: end;
  margin-bottom: var(--sp-3);
}
.upload-row textarea {
  width: 100%;
  box-sizing: border-box;
}
.mono {
  font-family: var(--font-mono);
}
.small {
  font-size: var(--fs-11);
}
.nowrap {
  white-space: nowrap;
}
.wide {
  min-width: 140px;
}
.notes {
  /* min-width：窄螢幕時別把說明欄擠成一字一行（表格本身在 .tbl-scroll 裡可橫向捲動）。 */
  min-width: 160px;
  max-width: 280px;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  font-size: var(--fs-12);
  color: var(--fg-2);
}
tr.gone td {
  color: var(--fg-4);
}
.empty-row {
  text-align: center;
  padding: var(--sp-5) 0;
}
.more-row {
  display: flex;
  justify-content: center;
  margin-top: var(--sp-3);
}
/* 清單讀取失敗的頁內錯誤列（R3-3）：錯誤文字＋〈重新讀取〉。 */
.list-err {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--sp-2);
  flex-wrap: wrap;
}
.form-grid.two {
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
}
.block {
  margin-top: var(--sp-3);
}
.icon-row {
  display: flex;
  align-items: center;
  gap: var(--sp-3);
}
.warn-list {
  margin: 0 0 var(--sp-3);
  padding-left: 1.2em;
  color: var(--fg-2);
  font-size: var(--fs-13);
  line-height: var(--lh-base);
}
.listing-row {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  flex-wrap: wrap;
  margin: var(--sp-2) 0;
}
.reject-note {
  margin: var(--sp-1, 4px) 0 var(--sp-2);
  color: var(--danger);
  font-size: var(--fs-12);
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
.wrap {
  /* 版本表狀態欄是 nowrap；婉拒理由可能很長，這一行自己換行、限寬。 */
  white-space: pre-wrap;
  max-width: 240px;
}
/* 狀態欄底下會帶婉拒理由：窄螢幕時別被擠成一行三五個字（表格本身在 .tbl-scroll 裡可橫向捲）。 */
.status-cell {
  min-width: 170px;
}
.once-warn {
  margin: 0 0 var(--sp-3);
  padding: var(--sp-3);
  border-radius: var(--r-md);
  background: var(--st-pending-bg);
  color: var(--st-pending-fg);
  font-size: var(--fs-13);
  line-height: var(--lh-base);
}
.issued-list {
  list-style: none;
  margin: 0 0 var(--sp-3);
  padding: 0;
  display: grid;
  gap: 6px;
  max-height: 320px;
  overflow: auto;
}
.issued-list .code {
  font-size: var(--fs-13);
  letter-spacing: 1px;
  user-select: all;
}
@media (max-width: 900px) {
  .cols {
    grid-template-columns: minmax(0, 1fr);
  }
  .upload-row {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
